// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module createAnalyticsDashboard
 * @package @geenius/analytics/solidjs-css
 * @description Normalizes dashboard page inputs into reactive SolidJS CSS
 * accessors for hosts that compose the analytics dashboard manually.
 */

import type {
  AnalyticsDashboardData,
  AnalyticsEvent,
  CohortAnalysis,
  ConsentSettings,
  FunnelResult,
  RealtimeStats,
  TimelineEvent,
} from "@geenius/analytics";
import { createMemo, type Accessor } from "solid-js";
import type { AnalyticsDashboardPageProps } from "../pages/AnalyticsDashboardPage";

type DashboardInput<T> = T | Accessor<T>;
type AnalyticsDashboardPageViewProps = Pick<
  AnalyticsDashboardPageProps,
  | "cohortAnalysis"
  | "data"
  | "error"
  | "funnelResult"
  | "isLoading"
  | "isRealtimeLoading"
  | "onRetry"
  | "recentEvents"
  | "realtimeStats"
>;

/** Input data consumed by {@link createAnalyticsDashboard}. */
export interface CreateAnalyticsDashboardOptions {
  data?: DashboardInput<AnalyticsDashboardData | null | undefined>;
  recentEvents?: DashboardInput<AnalyticsEvent[] | undefined>;
  realtimeStats?: DashboardInput<RealtimeStats | null | undefined>;
  funnelResult?: DashboardInput<FunnelResult | null | undefined>;
  cohortAnalysis?: DashboardInput<CohortAnalysis | null | undefined>;
  timelineEvents?: DashboardInput<TimelineEvent[] | undefined>;
  consentSettings?: DashboardInput<ConsentSettings | null | undefined>;
  isLoading?: DashboardInput<boolean | undefined>;
  isRealtimeLoading?: DashboardInput<boolean | undefined>;
  error?: DashboardInput<AnalyticsDashboardPageProps["error"] | undefined>;
  onRetry?: AnalyticsDashboardPageProps["onRetry"];
}

/** Numeric dashboard summary derived by {@link createAnalyticsDashboard}. */
export interface AnalyticsDashboardSummary {
  totalPageViews: number;
  uniqueVisitors: number;
  topPageCount: number;
  recentEventCount: number;
  activeVisitors: number;
}

/** Reactive dashboard state returned by {@link createAnalyticsDashboard}. */
export interface CreateAnalyticsDashboardReturn {
  data: Accessor<AnalyticsDashboardData | null>;
  recentEvents: Accessor<AnalyticsEvent[]>;
  realtimeStats: Accessor<RealtimeStats | null>;
  funnelResult: Accessor<FunnelResult | null>;
  cohortAnalysis: Accessor<CohortAnalysis | null>;
  timelineEvents: Accessor<TimelineEvent[]>;
  consentSettings: Accessor<ConsentSettings | null>;
  isLoading: Accessor<boolean>;
  isRealtimeLoading: Accessor<boolean>;
  error: Accessor<AnalyticsDashboardPageProps["error"]>;
  hasDashboardData: Accessor<boolean>;
  hasRealtimeData: Accessor<boolean>;
  isEmpty: Accessor<boolean>;
  summary: Accessor<AnalyticsDashboardSummary>;
  pageProps: Accessor<AnalyticsDashboardPageViewProps>;
}

function readInput<T>(
  input: DashboardInput<T | undefined> | undefined,
): T | undefined {
  return typeof input === "function"
    ? (input as Accessor<T | undefined>)()
    : input;
}

/**
 * Builds a reactive dashboard view model for the SolidJS CSS analytics dashboard.
 *
 * @param options Dashboard data, realtime stats, events, and state flags.
 * @returns Normalized dashboard accessors and page-ready props.
 */
export function createAnalyticsDashboard(
  options: CreateAnalyticsDashboardOptions = {},
): CreateAnalyticsDashboardReturn {
  const data = createMemo(() => readInput(options.data) ?? null);
  const recentEvents = createMemo(() => readInput(options.recentEvents) ?? []);
  const realtimeStats = createMemo(
    () => readInput(options.realtimeStats) ?? null,
  );
  const funnelResult = createMemo(
    () => readInput(options.funnelResult) ?? null,
  );
  const cohortAnalysis = createMemo(
    () => readInput(options.cohortAnalysis) ?? null,
  );
  const timelineEvents = createMemo(
    () => readInput(options.timelineEvents) ?? [],
  );
  const consentSettings = createMemo(
    () => readInput(options.consentSettings) ?? null,
  );
  const isLoading = createMemo(() => readInput(options.isLoading) ?? false);
  const isRealtimeLoading = createMemo(
    () => readInput(options.isRealtimeLoading) ?? false,
  );
  const error = createMemo(() => readInput(options.error) ?? null);

  const summary = createMemo<AnalyticsDashboardSummary>(() => ({
    activeVisitors: realtimeStats()?.activeVisitors ?? 0,
    recentEventCount: recentEvents().length,
    topPageCount: data()?.topPages.length ?? 0,
    totalPageViews: data()?.totalPageViews ?? 0,
    uniqueVisitors: data()?.uniqueVisitors ?? 0,
  }));

  const hasDashboardData = createMemo(() => {
    const currentData = data();
    return (
      currentData !== null &&
      (currentData.totalPageViews > 0 ||
        currentData.uniqueVisitors > 0 ||
        currentData.topPages.length > 0)
    );
  });
  const hasRealtimeData = createMemo(() => realtimeStats() !== null);
  const isEmpty = createMemo(
    () =>
      !isLoading() &&
      !error() &&
      data() === null &&
      realtimeStats() === null &&
      recentEvents().length === 0 &&
      funnelResult() === null &&
      cohortAnalysis() === null &&
      timelineEvents().length === 0,
  );
  const pageProps = createMemo<AnalyticsDashboardPageViewProps>(() => ({
    cohortAnalysis: cohortAnalysis(),
    data: data(),
    error: error(),
    funnelResult: funnelResult(),
    isLoading: isLoading(),
    isRealtimeLoading: isRealtimeLoading(),
    onRetry: options.onRetry,
    recentEvents: recentEvents(),
    realtimeStats: realtimeStats(),
  }));

  return {
    cohortAnalysis,
    consentSettings,
    data,
    error,
    funnelResult,
    hasDashboardData,
    hasRealtimeData,
    isEmpty,
    isLoading,
    isRealtimeLoading,
    pageProps,
    realtimeStats,
    recentEvents,
    summary,
    timelineEvents,
  };
}
